import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import './PeopleAndPlacesPage.css';

const IMAGES = {
  hero: '/people-and-places-hero-clean.jpg',
  communityImpact: '/people-and-places-community-clean.jpg',
  workforcePathways: '/people-and-places-workforce-clean.jpg',
  tamla: '/people-and-places-tamla-clean.jpg',
};

const PeopleAndPlacesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="pp-page" data-testid="people-and-places-page">
      <PageSEO
        title="People + Places Initiative"
        path="/people-and-places"
        description="The R6 Collective's civic initiative partnering with municipalities to close workforce gaps, improve public-sector talent pipelines, and elevate community impact. Led by Tamla Oates-Forney and Kevan Shelton."
      />
      <Header />

      {/* Section 1: Hero */}
      <section className="pp-hero" data-testid="pp-hero">
        <div className="pp-hero-banner">
          <img src={IMAGES.hero} alt="People + Places community" className="pp-hero-image" />
          <div className="pp-hero-overlay">
            <h1 className="pp-hero-logo">
              <span className="pp-logo-text">People </span>
              <span className="pp-logo-plus">+</span>
              <span className="pp-logo-text"> Places</span>
            </h1>
            <p className="pp-hero-tagline">The R6&#8482; Collective + Park Street Homes</p>
          </div>
        </div>
        <div className="pp-hero-content">
          <span className="pp-label">New Initiative</span>
          <p className="pp-hero-subtitle">
            Connecting housing, workforce pathways, and economic mobility through a new initiative from The R6&#8482; Collective and Park Street Homes.
          </p>
          <p className="pp-hero-intro">
            People + Places is built on a simple belief: communities are stronger when place and opportunity move together. By connecting community development with workforce development, this initiative is designed to help residents access clearer pathways to training, career growth, and long-term economic mobility while supporting stronger neighborhood outcomes.
          </p>
          <div className="pp-hero-buttons">
            <button className="pp-btn pp-btn-primary" onClick={() => window.open('https://form.jotform.com/260186646305054', '_blank')} data-testid="pp-connect-btn">
              Connect With Us
            </button>
            <button className="pp-btn pp-btn-outline-light" onClick={() => document.getElementById('pp-what').scrollIntoView({ behavior: 'smooth' })} data-testid="pp-explore-btn">
              Explore the Initiative
            </button>
          </div>
        </div>
      </section>

      {/* Section 2: What is People + Places */}
      <section className="pp-section pp-what" id="pp-what" data-testid="pp-what-section">
        <div className="pp-narrow-container">
          <h2 className="pp-section-title">What is People + Places</h2>
          <p className="pp-body">
            People + Places is a collaborative initiative from The R6&#8482; Collective and Park Street Homes designed to connect three forces that are too often addressed separately: community development, workforce pathways, and economic mobility.
          </p>
          <p className="pp-body">
            Rather than treating housing and workforce development as disconnected efforts, People + Places brings them together in a more coordinated model &mdash; one that helps communities think more strategically about how residents access training, opportunity, and long-term stability.
          </p>
        </div>
      </section>

      {/* Section 3: Why it matters */}
      <section className="pp-section pp-alt-bg" data-testid="pp-why-section">
        <div className="pp-two-col-container">
          <div className="pp-col-image">
            <img src={IMAGES.communityImpact} alt="Community impact" className="pp-col-img" />
          </div>
          <div className="pp-col-text">
            <h2 className="pp-section-title">Why it matters</h2>
            <p className="pp-body">
              Many communities face a common challenge: investment may be happening in housing, infrastructure, or neighborhood revitalization, yet residents still face barriers to entering or advancing in family-sustaining careers. At the same time, employers continue to experience workforce gaps, hiring challenges, and the need for stronger local talent pipelines.
            </p>
            <p className="pp-body">
              People + Places is designed to help close that gap by bringing workforce solutions closer to community realities and aligning talent pathways with local opportunity.
            </p>
            <div className="pp-callouts">
              <div className="pp-callout">
                <span className="pp-callout-label">For residents</span>
                <p>Clearer access to training, credentials, and career navigation</p>
              </div>
              <div className="pp-callout">
                <span className="pp-callout-label">For employers</span>
                <p>Stronger local talent pipelines aligned to hiring needs</p>
              </div>
              <div className="pp-callout">
                <span className="pp-callout-label">For communities</span>
                <p>Increased economic mobility and long-term neighborhood strength</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: How the model works */}
      <section className="pp-section" data-testid="pp-how-section">
        <div className="pp-two-col-container pp-reverse">
          <div className="pp-col-text">
            <h2 className="pp-section-title">How the model works</h2>
            <p className="pp-body pp-intro-line">
              The initiative is designed to support communities through four connected areas:
            </p>
            <div className="pp-model-items">
              <div className="pp-model-item">
                <h3>Career Pathways</h3>
                <p>Building practical routes into in-demand careers aligned with local employer needs.</p>
              </div>
              <div className="pp-model-item">
                <h3>Employer-Aligned Training</h3>
                <p>Connecting training efforts to real workforce gaps, hiring demand, and advancement opportunities.</p>
              </div>
              <div className="pp-model-item">
                <h3>Wraparound Support</h3>
                <p>Supporting participants through coaching, navigation, mentoring, and barrier reduction.</p>
              </div>
              <div className="pp-model-item">
                <h3>Community-Based Delivery</h3>
                <p>Designing workforce solutions that are accessible to residents and responsive to neighborhood realities.</p>
              </div>
            </div>
          </div>
          <div className="pp-col-image">
            <div className="pp-image-grid">
              <img src="/pp-workforce-1.jpg" alt="Digital skills and office work" className="pp-grid-img" />
              <img src="/pp-workforce-2.jpg" alt="Technical and skilled trades" className="pp-grid-img" />
              <img src="/pp-workforce-3.jpg" alt="Coaching and team development" className="pp-grid-img" />
              <img src="/pp-workforce-4.jpg" alt="Leadership and presenting" className="pp-grid-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Where it begins */}
      <section className="pp-section pp-alt-bg" data-testid="pp-where-section">
        <div className="pp-narrow-container">
          <h2 className="pp-section-title">Where it begins</h2>
          <p className="pp-body">
            The initial rollout for People + Places is focused on Houston, Texas and Birmingham, Alabama &mdash; two markets where community development and workforce development can work together to support stronger long-term outcomes.
          </p>
          <p className="pp-body">
            Rollout activity is expected to begin in late Q3 / Q4 2026, with programming and stakeholder engagement shaped by local employer demand, community needs, and implementation partners in each market.
          </p>
        </div>
      </section>

      {/* Section 6: What this can include */}
      <section className="pp-section" data-testid="pp-includes-section">
        <div className="pp-narrow-container">
          <h2 className="pp-section-title">What this can include</h2>
          <p className="pp-body pp-intro-line">
            Depending on market needs and implementation partners, People + Places may support pathways and programming connected to:
          </p>
          <div className="pp-skills-grid">
            <div className="pp-skill-item">Technical certifications</div>
            <div className="pp-skill-item">Digital and workplace skills</div>
            <div className="pp-skill-item">AI and future-ready learning</div>
            <div className="pp-skill-item">Data, analytics, and productivity</div>
            <div className="pp-skill-item">CRM and project management</div>
            <div className="pp-skill-item">Healthcare pathways</div>
            <div className="pp-skill-item">Leadership and employee development</div>
            <div className="pp-skill-item">Skilled trades</div>
          </div>
        </div>
      </section>

      {/* Section 7: Partner spotlight */}
      <section className="pp-section pp-alt-bg" data-testid="pp-partners-section">
        <div className="pp-narrow-container">
          <h2 className="pp-section-title">A partnership built around people, place, and pathways</h2>
          <div className="pp-partner-grid">
            <div className="pp-partner-card">
              <h3>The R6&#8482; Collective</h3>
              <p>
                The R6&#8482; Collective brings workforce strategy, career path design, talent development, and implementation support designed to help communities, employers, and partners move from fragmented training efforts to more coordinated talent pipelines.
              </p>
            </div>
            <div className="pp-partner-card">
              <h3><a href="https://www.parkstreethomes.com/" target="_blank" rel="noopener noreferrer" className="pp-partner-link">Park Street Homes</a></h3>
              <p>
                Park Street Homes brings a community-rooted development approach focused on quality housing, neighborhood investment, and expanding access to opportunity through place-based work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Leadership perspectives */}
      <section className="pp-section" data-testid="pp-leadership-section">
        <div className="pp-two-col-container pp-leadership">
          <div className="pp-col-image pp-portrait">
            <img src={IMAGES.tamla} alt="Tamla Oates-Forney, CEO" className="pp-col-img pp-tamla-img" />
          </div>
          <div className="pp-col-text pp-quote-col">
            <span className="pp-label">Leadership Perspective</span>
            <blockquote className="pp-quote">
              &ldquo;Building homes while developing people is where true community transformation happens.&rdquo;
            </blockquote>
            <div className="pp-attribution">
              <strong>Tamla Oates-Forney</strong>
              <span>CEO, The R6&#8482; Collective</span>
            </div>
            <p className="pp-body pp-supporting">
              People + Places reflects a shared belief that stronger communities are built when housing, workforce pathways, and long-term opportunity are connected.
            </p>
          </div>
        </div>
      </section>

      {/* Section 8b: Kevan leadership perspective */}
      <section className="pp-section pp-alt-bg" data-testid="pp-leadership-kevan-section">
        <div className="pp-two-col-container pp-leadership pp-leadership-reverse">
          <div className="pp-col-text pp-quote-col">
            <span className="pp-label">Leadership Perspective</span>
            <blockquote className="pp-quote">
              &ldquo;Why cities need to connect housing, skills and opportunity and what we are doing about it.&rdquo;
            </blockquote>
            <div className="pp-attribution">
              <strong>Kevan Shelton</strong>
              <span>CEO, Park Street Homes</span>
            </div>
            <p className="pp-body pp-supporting">
              People + Places brings together the vision and commitment needed to ensure that housing investment and workforce development move in the same direction.
            </p>
          </div>
          <div className="pp-col-image pp-portrait">
            <img src="/people-and-places-kevan.jpg" alt="Kevan Shelton, CEO of Park Street Homes" className="pp-col-img pp-tamla-img" />
          </div>
        </div>
      </section>

      {/* Section 9: CTA */}
      <section className="pp-cta-section" data-testid="pp-cta-section">
        <div className="pp-cta-container">
          <h2 className="pp-cta-title">Let&rsquo;s build what comes next</h2>
          <p className="pp-cta-body">
            People + Places is designed to create stronger alignment between housing, workforce development, and economic mobility. If your organization is exploring community-rooted workforce solutions, strategic partnerships, or implementation opportunities, we&rsquo;d welcome the conversation.
          </p>
          <div className="pp-cta-buttons">
            <button className="pp-btn pp-btn-primary" onClick={() => window.open('https://form.jotform.com/260186646305054', '_blank')} data-testid="pp-cta-contact-btn">
              Contact The R6&#8482; Collective
            </button>
            <button className="pp-btn pp-btn-outline-light" onClick={() => window.open('https://form.jotform.com/260186646305054', '_blank')} data-testid="pp-cta-schedule-btn">
              Schedule a Conversation
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PeopleAndPlacesPage;
